// Receipt assignment functionality
import { logMessage, parseUrlForGroupId } from './utils.mjs';
import { editExpense } from './api.mjs';
import { renderModal } from './ui.mjs';
import {
    cloneItemizedData,
    createParticipantRow,
    createAddPersonRow,
    areParticipantsEqual
} from './receiptHelpers.mjs';
import { selectedItems, selectionHistoryStatePushed, currentExpense } from './receiptState.mjs';
import t from 'virtual:i18n';

// Unassign a participant from an item
export async function unassignParticipant(expense, itemIndex, assigneeToRemove, participants) {
    const groupId = parseUrlForGroupId();
    if (!groupId) {
        logMessage('Cannot determine group ID', 'error');
        return;
    }

    // Deep copy and strip typename
    const itemizedDataInput = cloneItemizedData(expense.itemizedData);

    const item = itemizedDataInput.items[itemIndex];
    if (!item || !item.assignees) return;

    // We identify the assignee based on participants matching exactly
    // (Should suffice unless we have identical multi-user assignments which is rare/not supported well yet)
    let assigneeIndex = item.assignees.findIndex(a =>
        JSON.stringify(a.participants.sort()) === JSON.stringify(assigneeToRemove.participants.slice().sort())
    );

    if (assigneeIndex === -1) {
        logMessage('Could not find assignment to remove', 'error');
        return;
    }

    // Reduce quantity by 1
    item.assignees[assigneeIndex].quantity -= 1;

    // If quantity is 0 or less, remove the assignment entirely
    if (item.assignees[assigneeIndex].quantity <= 0) {
        item.assignees.splice(assigneeIndex, 1);
    }

    try {
        logMessage('Removing assignment...', 'info');
        const updatedGroup = await editExpense(groupId, expense.id, {
            itemizedData: itemizedDataInput,
            expenseType: expense.expenseType,
            currency: expense.currency || 'USD',
            date: expense.date,
            description: expense.description
        });

        logMessage('Assignment removed successfully', 'success');

        const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
        if (updatedExpense) {
            const { renderReceiptView } = await import('./receiptView.mjs');
            renderReceiptView(updatedExpense, participants, false); // Don't reset selections
        }

    } catch (e) {
        console.error(e);
        logMessage(`Error removing assignment: ${e.message}`, 'error');
    }
}

// Open Assignment Modal
export async function openAssignmentModal(expense, participants) {
    const { createFAB, Icons } = await import('./components.mjs');

    const content = document.createElement('div');
    Object.assign(content.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '8px 0'
    });

    // 1. Participant List
    const list = document.createElement('div');
    Object.assign(list.style, {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    });

    // Track selections: Set of participant indices
    const selectedIndices = new Set();

    // Track if assignment was completed (to prevent clearing selection on successful assignment)
    let assignmentCompleted = false;

    // Create FAB using unified component
    const fab = createFAB({
        text: t.assign,
        icon: Icons.checkmark,
        onClick: performAssignment
    });

    // Initially hide the FAB
    fab.style.display = 'none';

    // Helper to update FAB
    function updateFabState() {
        const count = selectedIndices.size;
        if (count === 0) {
            fab.style.display = 'none';
        } else {
            fab.style.display = 'flex';
            // Update the text dynamically
            fab.querySelector('span').textContent = t.assignToCount.replace('{count}', count);
            // Add subtle pop animation
            fab.animate([
                { transform: 'translateX(-50%) scale(0.8)', opacity: 0 },
                { transform: 'translateX(-50%) scale(1)', opacity: 1 }
            ], { duration: 150, easing: 'ease-out' });
        }
    }


    participants.forEach((name, index) => {
        const { row, check } = createParticipantRow({
            name,
            index,
            isSelected: false,
            onToggle: (row, check) => {
                if (selectedIndices.has(index)) {
                    selectedIndices.delete(index);
                    row.style.backgroundColor = 'transparent';
                    row.style.borderColor = 'transparent';
                    check.style.visibility = 'hidden';
                } else {
                    selectedIndices.add(index);
                    row.style.backgroundColor = '#f3effc';
                    row.style.borderColor = '#d0bcff';
                    check.style.visibility = 'visible';
                }
                updateFabState();
            }
        });
        list.appendChild(row);
    });

    content.appendChild(list);

    // 2. Add New Participant Row
    const addRow = createAddPersonRow((newName) => {
        const newIndex = participants.length;
        participants.push(newName);

        const { row: newRow, check } = createParticipantRow({
            name: newName,
            index: newIndex,
            isSelected: true,
            onToggle: (row, check) => {
                if (selectedIndices.has(newIndex)) {
                    selectedIndices.delete(newIndex);
                    row.style.backgroundColor = 'transparent';
                    row.style.borderColor = 'transparent';
                    check.style.visibility = 'hidden';
                } else {
                    selectedIndices.add(newIndex);
                    row.style.backgroundColor = '#f3effc';
                    row.style.borderColor = '#d0bcff';
                    check.style.visibility = 'visible';
                }
                updateFabState();
            }
        });

        selectedIndices.add(newIndex);
        list.insertBefore(newRow, addRow);
        updateFabState();
    });
    list.appendChild(addRow);




    // Function to execute assignment
    async function performAssignment() {
        if (selectedIndices.size === 0) return;

        const selectedParticipantIndices = Array.from(selectedIndices);
        const groupId = parseUrlForGroupId();

        try {
            logMessage('Saving assignment...', 'info');

            // 1. Prepare updated itemizedData
            const itemizedDataInput = cloneItemizedData(expense.itemizedData);

            let hasChanges = false;

            // 2. Iterate through selectedItems and update assignees
            for (const [itemIdxStr, qty] of Object.entries(selectedItems)) {
                const itemIdx = parseInt(itemIdxStr, 10);
                if (qty <= 0) continue;

                const item = itemizedDataInput.items[itemIdx];
                if (!item) continue;

                if (!item.assignees) {
                    item.assignees = [];
                }

                // Logic:
                // Check if this EXACT group already handles part of the item.
                let existingAssignee = item.assignees.find(a =>
                    areParticipantsEqual(a.participants, selectedParticipantIndices)
                );

                if (existingAssignee) {
                    existingAssignee.quantity += qty;
                } else {
                    item.assignees.push({
                        participants: selectedParticipantIndices,
                        quantity: qty
                    });
                }
                hasChanges = true;
            }

            if (!hasChanges) {
                // Just close
                return;
                // But modal closes automatically via renderModal button logic
            }

            // 3. Prepare input for editExpense mutation
            const input = {
                itemizedData: itemizedDataInput,
                expenseType: expense.expenseType,
                currency: expense.currency || 'USD',
                date: expense.date,
                description: expense.description
            };

            // If there is no payer, set the payer to the first assignee in the list
            const hasPayer = expense.payers && expense.payers.length > 0;
            if (!hasPayer) {
                let firstAssigneeIndex = null;
                if (itemizedDataInput.items) {
                    for (const item of itemizedDataInput.items) {
                        if (item.assignees) {
                            for (const assignee of item.assignees) {
                                if (assignee.participants && assignee.participants.length > 0) {
                                    firstAssigneeIndex = assignee.participants[0];
                                    break;
                                }
                            }
                        }
                        if (firstAssigneeIndex !== null) break;
                    }
                }

                if (firstAssigneeIndex !== null) {
                    input.payers = [firstAssigneeIndex];
                    input.payerAmounts = [expense.amount];
                }
            }

            // 4. Call editExpense mutation w/ potentially NEW participants list
            const updatedGroup = await editExpense(groupId, expense.id, input, participants); // Important: pass the potentially updated 'participants' (which we pushed to)

            logMessage('Assignment saved successfully', 'success');

            // Mark assignment as completed
            assignmentCompleted = true;

            // Close the modal first
            const { closeModal } = await import('./ui.mjs');
            closeModal(modal);

            // Handle history state cleanup / Nav Back
            const { renderReceiptView } = await import('./receiptView.mjs');
            const { selectedItems: stateSelectedItems, selectionHistoryStatePushed: statePushed, setCurrentView } = await import('./receiptState.mjs');

            if (statePushed) {
                const updExp = updatedGroup.expenses.find(e => e.id === expense.id);
                setCurrentView(updExp, updatedGroup.participants);
                history.back(); // Pop state -> Clears selection -> view updates
            } else {
                // Clear selected items
                Object.keys(stateSelectedItems).forEach(key => delete stateSelectedItems[key]);
                // Refresh view
                const updatedExpense = updatedGroup.expenses.find(e => e.id === expense.id);
                if (updatedExpense) {
                    renderReceiptView(updatedExpense, updatedGroup.participants, true);
                }
            }

        } catch (e) {
            console.error(e);
            logMessage(`Error saving assignment: ${e.message}`, 'error');
        }
    };

    // Render the modal
    const modal = renderModal({
        anchorEl: document.body,
        title: t.assignTo,
        body: content,
        buttons: [], // No default buttons
        onClose: async () => {
            // Only clear selection if assignment was not completed
            if (assignmentCompleted) {
                return;
            }

            // Clear selection when modal is closed without assignment
            const { selectedItems: stateSelectedItems, selectionHistoryStatePushed: statePushed } = await import('./receiptState.mjs');
            const { renderReceiptView } = await import('./receiptView.mjs');

            if (statePushed) {
                // If we pushed history state, go back to clear selection
                history.back();
            } else {
                // Clear selected items
                Object.keys(stateSelectedItems).forEach(key => delete stateSelectedItems[key]);
                // Refresh view
                if (currentExpense) {
                    renderReceiptView(currentExpense, participants, true);
                }
            }
        }
    });

    // Append FAB to the modal content container
    // We need to wait for renderModal to create structure, OR we can append to document if we want fixed.
    // However, sticking to modal content is safer for correct z-indexing and lifecycle.
    const sheetContent = modal.querySelector('.bottom-sheet-content'); // ui.mjs structure
    if (sheetContent) {
        sheetContent.appendChild(fab);
    }
    updateFabState(); // Initial call to set FAB visibility
}
